import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../../utils/apiError';

// Quote terms (2026-10-08): every quote PDF ends with a TERMS & CONDITIONS block (quotes are good for
// 7 days while steel prices move). The standard text is kept on the API (GET/PUT /settings/quote-terms);
// a Draft quote can carry its own text, and its text can be made the new standard
// (PUT /quotes/{id}/terms with make_default). Text equal to the standard, or blank, = follow the standard.

// The API's own normalization (jobq-api _normalize_quote_terms): CRLF -> LF, trailing spaces and blank
// edges dropped. Used only to say "this is the standard text" before saving; the API decides.
export const normalizeTerms = (text) =>
    (text || '').replace(/\r\n?/g, '\n').split('\n').map(l => l.replace(/\s+$/, '')).join('\n').trim();

const fromSettings = (s) => ({
    quote_id: null, editable: false, is_custom: false, terms_text: null,
    effective_terms: s.terms_text, default_terms: s.terms_text,
    default_updated_at: s.updated_at, default_updated_by: s.updated_by, max_length: s.max_length,
});

const fmtWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const currentUserName = () => {
    try { return sessionStorage.getItem('userName') || null; } catch { return null; }
};

export default function QuoteTermsCard({ quoteId, editable, apiBase, onNotify }) {
    const [terms, setTerms] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [makeDefault, setMakeDefault] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const load = useCallback(async () => {
        setLoadError('');
        try {
            const res = await fetch(quoteId ? `${apiBase}/quotes/${quoteId}/terms` : `${apiBase}/settings/quote-terms`);
            if (!res.ok) throw new Error(await apiErrorMessage(res, 'Could not load the quote terms'));
            const body = await res.json();
            setTerms(quoteId ? body : fromSettings(body));
        } catch (err) {
            setTerms(null);
            setLoadError(err.message);
        }
    }, [quoteId, apiBase]);

    useEffect(() => { load(); }, [load]);

    const openEditor = () => {
        setDraft(terms?.effective_terms || '');
        setMakeDefault(false);
        setSaveError('');
        setEditing(true);
    };

    const save = async (text, asDefault) => {
        setSaving(true);
        setSaveError('');
        try {
            const res = await fetch(`${apiBase}/quotes/${quoteId}/terms`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ terms_text: text, make_default: asDefault, changed_by: currentUserName() }),
            });
            if (!res.ok) throw new Error(await apiErrorMessage(res, 'Could not save the terms'));
            const body = await res.json();
            setTerms(body);
            setEditing(false);
            if (onNotify) {
                onNotify(asDefault
                    ? 'Saved. This is now the standard text for every quote that uses the standard terms.'
                    : (body.is_custom ? 'Saved: this quote has its own terms.' : 'This quote uses the standard terms.'),
                'success');
            }
        } catch (err) {
            if (editing) setSaveError(err.message);
            else if (onNotify) onNotify(err.message, 'danger');
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return (
            <div className="mt-3 alert alert-warning py-1 small mb-0" data-testid="quote-terms-card">
                Terms &amp; Conditions: {loadError}
            </div>
        );
    }
    if (!terms) return null;

    const max = terms.max_length || 2000;
    const draftNorm = normalizeTerms(draft);
    const isStandardText = draftNorm === normalizeTerms(terms.default_terms);
    const lastChanged = [fmtWhen(terms.default_updated_at), terms.default_updated_by].filter(Boolean);

    return (
        <div className="mt-3 p-2 border rounded" data-testid="quote-terms-card">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <h6 className="mb-0">7. Terms &amp; Conditions</h6>
                <span className={`badge ${terms.is_custom ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                    {terms.is_custom ? 'Custom for this quote' : 'Standard terms'}
                </span>
                <small className="text-muted">Printed at the bottom of the quote PDF</small>
                {editable && (
                    <div className="ms-auto d-flex gap-2">
                        {terms.is_custom && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={saving}
                                onClick={() => save(null, false)}>Use standard terms</button>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={openEditor}>Edit terms</button>
                    </div>
                )}
            </div>
            <div className="text-muted" style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }} data-testid="quote-terms-text">
                {terms.effective_terms || <em>No terms will print on this quote.</em>}
            </div>
            {!quoteId && <small className="text-muted d-block mt-1">Save the quote first to give it its own terms.</small>}

            {editing && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog"
                    aria-labelledby="quote-terms-title">
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="quote-terms-title">Terms &amp; Conditions, Quote #{quoteId}</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setEditing(false)}></button>
                            </div>
                            <div className="modal-body">
                                <textarea className="form-control" rows="7" maxLength={max} value={draft}
                                    aria-label="Terms for this quote" onChange={e => setDraft(e.target.value)} />
                                <div className="d-flex justify-content-between small text-muted mt-1">
                                    <span>
                                        {isStandardText
                                            ? 'This is the standard text: the quote will follow the standard terms.'
                                            : (draftNorm ? 'This quote will print its own terms.'
                                                : 'Left blank, the quote follows the standard terms.')}
                                    </span>
                                    <span>{draft.length}/{max}</span>
                                </div>
                                {!isStandardText && (
                                    <button type="button" className="btn btn-link btn-sm px-0"
                                        onClick={() => setDraft(terms.default_terms || '')}>Start from the standard text</button>
                                )}
                                {draftNorm && !isStandardText && (
                                    <div className="form-check mt-2 p-2 border rounded bg-light">
                                        <input className="form-check-input ms-0 me-2" type="checkbox" id="quote-terms-make-default"
                                            checked={makeDefault} onChange={e => setMakeDefault(e.target.checked)} />
                                        <label className="form-check-label fw-bold" htmlFor="quote-terms-make-default">
                                            Also make this the standard terms for all quotes
                                        </label>
                                        <div className="small text-muted mt-1">
                                            Every quote that uses the standard terms will print this text from now on,
                                            including older quotes if they are downloaded again (copies already sent don't change).
                                            Quotes with their own terms keep them.
                                            {lastChanged.length > 0 && <> Standard text last changed {lastChanged.join(' by ')}.</>}
                                        </div>
                                    </div>
                                )}
                                {saveError && <div className="alert alert-danger py-1 mt-2 mb-0 small">{saveError}</div>}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                                <button type="button" className={`btn ${makeDefault && !isStandardText && draftNorm ? 'btn-warning' : 'btn-primary'}`}
                                    disabled={saving}
                                    onClick={() => save(draft, makeDefault && !isStandardText && !!draftNorm)}>
                                    {saving ? 'Saving...'
                                        : (makeDefault && !isStandardText && draftNorm ? 'Save and make standard' : 'Save for this quote')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
